import { useState, useEffect } from 'react'
import { useI18n } from '../../i18n'
import { useBranding } from '../../contexts/BrandingContext'
import { Button } from '../ui/button'
import { X, Trash2, ChevronUp, ChevronDown, Eye, EyeOff } from 'lucide-react'
import type { WebsiteBlock } from '../../types'

interface Props {
  block: WebsiteBlock
  onUpdate: (blockId: string, data: { props?: Record<string, any>; visible?: boolean }) => Promise<void>
  onClose: () => void
}

export default function WebsiteBlockEditor({ block, onUpdate, onClose }: Props) {
  const { t } = useI18n()
  const { branding } = useBranding()
  const [props, setProps] = useState(block.props)
  const [visible, setVisible] = useState(block.visible)

  useEffect(() => {
    setProps(block.props)
    setVisible(block.visible)
  }, [block.id, block.props])

  const handleChange = (key: string, value: any) => {
    setProps((prev: Record<string, any>) => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    await onUpdate(block.id, { props })
  }

  const handleToggleVisible = async () => {
    const newVisible = !visible
    setVisible(newVisible)
    await onUpdate(block.id, { visible: newVisible })
  }

  // Render different editors based on block type
  const renderFields = () => {
    switch (block.blockType) {
      case 'hero':
        return (
          <>
            <Field label={t('标题', 'Title')} value={props.title || ''} onChange={(v) => handleChange('title', v)} />
            <Field label={t('标题(英)', 'Title (EN)')} value={props.titleEn || ''} onChange={(v) => handleChange('titleEn', v)} />
            <Field label={t('副标题', 'Subtitle')} value={props.subtitle || ''} onChange={(v) => handleChange('subtitle', v)} multiline />
            <Field label={t('按钮文字', 'CTA Text')} value={props.ctaText || ''} onChange={(v) => handleChange('ctaText', v)} />
            <Field label={t('按钮链接', 'CTA URL')} value={props.ctaUrl || ''} onChange={(v) => handleChange('ctaUrl', v)} />
          </>
        )
      case 'features':
        return (
          <>
            <Field label={t('标题', 'Title')} value={props.title || ''} onChange={(v) => handleChange('title', v)} />
            <p className="text-[10px] text-gray-300 mt-2">{t('特性列表在AI编辑中修改更方便', 'Edit features via AI panel for better experience')}</p>
          </>
        )
      case 'faq':
        return (
          <>
            <Field label={t('标题', 'Title')} value={props.title || ''} onChange={(v) => handleChange('title', v)} />
            <p className="text-[10px] text-gray-300 mt-2">{t('FAQ条目在AI编辑中修改更方便', 'Edit FAQ items via AI panel for better experience')}</p>
          </>
        )
      case 'contact':
        return (
          <>
            <Field label={t('标题', 'Title')} value={props.title || ''} onChange={(v) => handleChange('title', v)} />
            <Field label={t('邮箱', 'Email')} value={props.email || ''} onChange={(v) => handleChange('email', v)} />
            <Field label={t('电话', 'Phone')} value={props.phone || ''} onChange={(v) => handleChange('phone', v)} />
            <Field label={t('地址', 'Address')} value={props.address || ''} onChange={(v) => handleChange('address', v)} />
          </>
        )
      default:
        return (
          <p className="text-[10px] text-gray-300">{t('此区块可通过AI编辑面板修改', 'Edit this block via AI panel')}</p>
        )
    }
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-[#1a1a1a]">{block.blockType}</h3>
          <p className="text-[10px] text-gray-400">{t('区块属性', 'Block Properties')}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50">
          <X className="h-4 w-4" />
        </button>
      </div>

      {renderFields()}

      <div className="mt-6 space-y-2">
        <Button onClick={handleSave} className="w-full bg-[#1a1a1a] text-white hover:bg-[#333] h-9 text-xs rounded-xl">
          {t('保存修改', 'Save Changes')}
        </Button>
        <button onClick={handleToggleVisible}
          className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-gray-400 hover:text-[#1a1a1a] rounded-xl hover:bg-black/[0.03] transition-all">
          {visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          {visible ? t('隐藏区块', 'Hide Block') : t('显示区块', 'Show Block')}
        </button>
      </div>
    </div>
  )
}

function Field({ label, value, onChange, multiline = false }: { label: string; value: string; onChange: (v: string) => void; multiline?: boolean }) {
  return (
    <div className="mb-3">
      <label className="block text-[11px] text-gray-500 mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 rounded-xl border border-black/[0.06] text-xs bg-white focus:border-orange-200 focus:outline-none resize-none"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-xl border border-black/[0.06] text-xs bg-white focus:border-orange-200 focus:outline-none"
        />
      )}
    </div>
  )
}
